import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { extractErrorMessage } from "../../api/client";
import { listSearchEnvEvents, SearchEnvEvent } from "../../api/searchEnvs";

// Renders the search_env_events timeline below an env's cost panel.
// Polls every 5s while expanded so the user sees live transitions
// (domain.active, index.submitted, index.failed, …) without a refresh.
// Collapsed by default to keep the env card compact; opens to a
// scrollable list of the most recent events newest-first.

interface Props {
  envId: string;
  // Expanded by default when the parent knows there's something worth
  // surfacing (e.g. the env is in a failed state). Caller passes
  // true to nudge the user toward the timeline.
  defaultExpanded?: boolean;
}

// Per-event-kind colour. Falls back to the level's colour when the
// kind isn't known so new event kinds added later don't render
// invisibly.
const KIND_COLOUR: Record<
  string,
  "default" | "primary" | "success" | "error" | "warning" | "info"
> = {
  "provision.submitted": "primary",
  "provision.failed": "error",
  "domain.active": "success",
  "domain.deleted": "default",
  "index.submitted": "primary",
  "index.succeeded": "success",
  "index.failed": "error",
  "index.deadline_extended": "info",
  "teardown.requested": "warning",
};

const LEVEL_COLOUR: Record<
  SearchEnvEvent["level"],
  "default" | "warning" | "error"
> = {
  info: "default",
  warning: "warning",
  error: "error",
};

function fmtTs(ms: number, nowMs: number): string {
  const delta = Math.max(0, nowMs - ms);
  const sec = Math.floor(delta / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ${min % 60}m ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ${hr % 24}h ago`;
}

export function SearchEnvActivityPanel({ envId, defaultExpanded }: Props) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const [events, setEvents] = useState<SearchEnvEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Tick a clock so relative timestamps stay fresh without polling
  // the events endpoint just for them.
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (!expanded) return;
    const h = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(h);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    const fetchOnce = () => {
      listSearchEnvEvents(envId, { limit: 100 })
        .then((resp) => {
          if (!cancelled) {
            setEvents(resp.events);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(extractErrorMessage(err));
        });
    };
    fetchOnce();
    const h = setInterval(fetchOnce, 5000);
    return () => {
      cancelled = true;
      clearInterval(h);
    };
  }, [envId, expanded]);

  const eventCount = useMemo(() => events?.length ?? 0, [events]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setExpanded((v) => !v)}
      >
        <IconButton size="small">
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          Activity {eventCount > 0 ? `(${eventCount})` : ""}
        </Typography>
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          {events === null && !error && (
            <Typography variant="caption" color="text.secondary">
              loading…
            </Typography>
          )}
          {events && events.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No events yet.
            </Typography>
          )}
          {events && events.length > 0 && (
            <Stack spacing={0.75} sx={{ maxHeight: 320, overflowY: "auto" }}>
              {events.map((ev) => (
                <Stack
                  key={ev.id}
                  direction="row"
                  spacing={1}
                  alignItems="baseline"
                  sx={{ fontSize: 13 }}
                >
                  <Chip
                    label={ev.kind}
                    size="small"
                    color={KIND_COLOUR[ev.kind] ?? LEVEL_COLOUR[ev.level]}
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontFamily: "monospace",
                      fontSize: 11,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 90 }}
                  >
                    {fmtTs(ev.ts, nowMs)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, wordBreak: "break-word" }}
                  >
                    {ev.message ?? ev.kind}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
